/**
 * Schedule version chip presentation — shared by BOTH schedule tables.
 *
 * WHY THIS IS SHARED RATHER THAN COPIED
 * -------------------------------------
 * `contract_schedule_versions.version_type` (amortization) and `contract_fee_versions.version_type`
 * (broker fee) use the SAME vocabulary — 'initial' | 'rate_change' | 'fee_repricing' | 'amendment'
 * | 'termination' — because the fee ledger deliberately copied the amortization versioning model.
 * Two copies of this mapping would drift the moment one table gained a version type the other
 * table's copy did not know about, and the symptom would be a silently mislabelled chip rather
 * than an error.
 *
 * ⚠ THE VERSION NUMBER IS THE ORDERING KEY AND CARRIES NO MEANING OF ITS OWN. Version KIND lives
 * in `version_type`; never infer "this is a repricing" from the number. That separation is why
 * `uq_contract_version` can stay a simple ordering constraint.
 */

/**
 * Chip props for a schedule/fee version.
 *
 * @param {number|null} versionNumber - the version's ordering number
 * @param {string|null} versionType   - 'initial' | 'rate_change' | 'fee_repricing' | 'amendment' | 'termination'
 * @returns {{label: string, color: string, title: string}|null} null when there is no version
 */
export function versionChipProps(versionNumber, versionType) {
  if (!versionNumber) return null;
  // A terminated version is the one kind worth calling out in the label itself: it means the
  // schedule STOPS here, so a reader must not expect the remaining term to be covered.
  const terminal = versionType === 'termination';
  return {
    label: terminal ? 'VT' : `V${versionNumber}`,
    color: terminal ? 'warning' : 'info',
    title: terminal
      ? `Version ${versionNumber} — contract terminated; no further schedule follows`
      : `Schedule version ${versionNumber}`,
  };
}

export default versionChipProps;
